import * as $ from 'jquery';
import * as rxui from 'common/rxui/core';
import * as moment from 'moment';
import { Observable, Subject, BehaviorSubject, Subscription } from 'common/barrels/rx';
import { serialDisposable, disposableSubscription, disposable, IDisposable, emptyDisposable } from 'common/barrels/core';
import { bind, Binding, canWrite } from 'common/dom/binding';
import { div } from 'common/barrels/dom';
import { click, restrictInput, focusout, enterPressed, filteredKeys } from 'common/rxui/core';
import { dateTimeTransmissionFormat } from 'common/functions/date';

import 'common/styles/controls/datetime.less';

export function datetime(
    value: Binding<string>,
    hasDate: Observable<boolean> = Observable.of(true),
    hasTime: Observable<boolean> = Observable.of(true),
    isEditable: Observable<boolean> = null,
): JQuery {
    const $element = div('studio-datetime');

    const editable = value == null || !canWrite(value) ? Observable.of(false)
        : isEditable == null ? Observable.of(true)
        : isEditable;

    const control = new DateTimePicker($element, hasDate, hasTime, editable);

    bind<string>(value, x => control.setValues.next(x), () => control.controlValues);

    return $element.owns(control);
}

const datePickerDefaults = {
    showOn: 'neither',
    changeYear: 'true',
    yearRange: 'c-50:c+50', //: '2004:+5',
    showAnim: null,
    showOtherMonths: true//,
    //selectOtherMonths: true
};

const timePickerDefaults = {
    appendTo: '.application-container',
    showOnFocus: false,
    orientation: 'r'
};

class DateTimePicker implements IDisposable {
    private value: BehaviorSubject<moment.Moment>;
    private subscriptions: IDisposable;

    private dateDisposable = serialDisposable(null);
    private timeDisposable = serialDisposable(null);

    public controlValues = new Subject<string>();
    public setValues = new Subject<string>();

    constructor(
        public $element: JQuery,
        private hasDate: Observable<boolean>,
        private hasTime: Observable<boolean>,
        private editable: Observable<boolean>,
    ) {
        this.$element.addClass('studio-datetime');
        var $layoutContainer = $('<div />').appendTo(this.$element);
        var $layout = $('<div />').appendTo($layoutContainer);
        var $date = $('<div />').addClass('datetimepicker-date').appendTo($layout);

        this.value = new BehaviorSubject<moment.Moment>(null);

        const updatingValue = new BehaviorSubject<boolean>(false);

        this.subscriptions = disposableSubscription(
            this.hasDate.distinctUntilChanged().subscribe(x => this.dateDisposable.set(x ? this.initDatePicker($layout, $date) : emptyDisposable)),
            this.hasTime.distinctUntilChanged().subscribe(x => this.timeDisposable.set(x ? this.initTimePicker($layout) : emptyDisposable)),

            this.setValues.subscribe(x => {
                updatingValue.next(true);
                this.value.next(x === null ? null : moment(x));
                updatingValue.next(false);
            }),

            this.value.whereLatest(updatingValue.map(x => !x)).subscribe(x => this.controlValues.next(x === null ? null : x.format(this.valueFormat()))),
        );
    }

    dispose() {
        this.subscriptions.dispose();
    }

    private initDatePicker($layout: JQuery, $date: JQuery) {
        var $dateWrapper = $('<div />').appendTo($date);
        var $dateInput = $('<input />').attr('placeholder', this.dateFormatForPlaceholder()).appendTo($dateWrapper);
        var $dateIcon = $('<i />').addClass('studio-datetime-date-icon fa fa-calendar').bindStyle(this.editable, null, 'studio-icon-disabled').insertAfter($date);

        const clicks = click($dateIcon).filter(() => !$dateInput.prop('readonly')).share();

        var pickerValues = new Subject<string>();

        var pickerOptions = $.extend({}, datePickerDefaults, {
            dateFormat: this.dateFormatForPicker(),
            onSelect: () => $dateInput.focus(),
            onClose: () => $dateInput.datepicker('destroy')
        });

        return disposableSubscription(
            disposable(() => {
                $dateWrapper.remove();
                $dateIcon.remove();
            }),
            pickerValues,
            clicks.subscribe(() => $dateInput.datepicker(pickerOptions).on('remove', () => $dateInput.datepicker('hide'))),
            clicks.subscribe(() => $dateInput.datepicker('show')),
            restrictInput($dateInput, this.value,
                this.focusOutOrEnterPressed($dateInput),
                x => (x === '' || x === null) ? true : moment(x, this.dateFormat()).isValid(),
                x => x === '' ? null : this.applyTimeToDate(this.parseDate(x), this.value.getValue()),
                x => x === null ? '' : x.format(this.dateFormat()),
                x => { },
                (x, y) => this.valuesEquivalent(x, y)
            ),
            this.manageEdibility($dateInput),
        );
    }

    private focusOutOrEnterPressed($e): Observable<{}> {
        return focusout($e).map(() => ({})).merge(enterPressed($e));
    }

    private parseDate(s: string): moment.Moment {
        if (s === '')
            return null;

        var m = moment(s, this.dateFormat());
        if (m.year() === 0)
            m.year(1);

        return m;
    }

    private initTimePicker($layout: JQuery) {
        var $time = $('<div />').addClass('studio-datetime-time').appendTo($layout);
        var $timeWrapper = $('<div />').appendTo($time);
        var $timeInput = $('<input />').appendTo($timeWrapper);
        var $timeIcon = $('<i />').addClass('studio-datetime-time-icon fa fa-clock-o').bindStyle(this.editable, null, 'studio-icon-disabled').appendTo($layout);

        var pickerOptions = $.extend({}, timePickerDefaults, {
            timeFormat: this.timeFormatForPicker()
        });

        $timeInput.on('changeTime', () => {
            $timeInput.triggerHandler('focusout');
            $timeInput.triggerHandler('focusin');
        });

        var clicks = click($timeIcon).share();

        var down = filteredKeys($timeInput, [$.ui.keyCode.DOWN]).map(() => true);

        var converter = x => x === '' && !this.hasDate.extract() ? null
            : x === '' && this.value.getValue() === null ? null
            : x === '' ? moment(this.value.getValue()).hour(0).minute(0).second(0).millisecond(0)
            : this.applyDateToTime(moment(x, this.timeFormat()), this.value.getValue());

        var deconverter = x => x === null ? '' : x.format(this.timeFormat());

        return disposableSubscription(
            disposable(() => {
                $time.remove();
                $timeIcon.remove();
            }),
            clicks.map(() => true).race(down).first().subscribe(() => (<any>$timeInput).timepicker(pickerOptions).on('remove', () => (<any>$timeInput).timepicker('remove'))),
            down.first().subscribe(() => (<any>$timeInput).timepicker('show')),
            clicks.subscribe(() => {
                $timeInput.focus();
                (<any>$timeInput).timepicker('show');
            }),
            restrictInput($timeInput, this.value,
                this.focusOutOrEnterPressed($timeInput),
                x => (x === '' || x === null) ? true : moment(x, this.timeFormat()).isValid(),
                converter,
                deconverter,
                x => { },
                (x, y) => this.valuesEquivalent(x, y)
            ),
            this.manageEdibility($timeInput),
        );
    }

    private manageEdibility($input: JQuery) : Subscription {
        return this.editable.subscribe(ie => {
            $input.prop('disabled', !ie);
        });
    }

    private applyTimeToDate(datePart, timePart): moment.Moment {
        return (timePart === null) ? datePart : moment(datePart).hour(timePart.hour()).minute(timePart.minute()).second(timePart.second()).millisecond(timePart.millisecond());
    }

    private applyDateToTime(timePart, datePart) {
        return datePart === null ? timePart : moment(timePart).year(datePart.year()).dayOfYear(datePart.dayOfYear());
    }

    private valuesEquivalent(a, b) {
        if (a === b)
            return true;

        if (moment.isMoment(a) && moment.isMoment(b))
            return a.isSame(b);

        return false;
    }

    private valueFormat() {
        return dateTimeTransmissionFormat();
    }

    private dateFormat() {
        return 'YYYY-MM-DD';
    }

    private timeFormat() {
        return 'HH:mm:ss';
    }

    private dateFormatForPicker() {
        return this.dateFormat().replace(/D/g, 'd').replace(/MM/g, 'mm').replace(/M/g, 'm').replace(/YYYY/g, 'yy');
    }

    private dateFormatForPlaceholder() {
        return 'YYYY-MM-DD';
    }

    private timeFormatForPicker() {
        return this.timeFormat()
            .replace(/([^h]|^)h(?!h)/g, 'g')
            .replace(/([^H]|^)H(?!H)/g, 'G')
            .replace(/HH/g, 'H')
            .replace(/hh/g, 'h')
            .replace(/mm/g, 'i')
            .replace(/ss/g, 's')
            .replace(/tt/g, 'A');
    }
}